import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { FloppyDisk, Buildings, Bank, Image as ImageIcon } from "@phosphor-icons/react";
import { useI18n } from "@/lib/i18n";

const EMPTY = {
  company_name: "", company_address: "", company_email: "", company_phone: "",
  company_website: "", company_tax_id: "", company_logo_url: "",
  logo_size: "medium", logo_position: "left",
  bank_name: "", bank_branch: "", bank_iban: "", bank_swift: "", bank_account_holder: "",
  default_min_support: 1.0,
};

// Defined OUTSIDE the component so React does NOT remount inputs on every keystroke
// (this was causing the input focus loss bug)
const Field = ({ label, value, onChange, placeholder, mono, type = "text", testid }) => (
  <label className="block">
    <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
    <input
      type={type}
      value={value || ""}
      onChange={onChange}
      placeholder={placeholder}
      data-testid={testid}
      className={`mt-1 w-full border border-slate-300 focus:border-[#2563EB] focus:ring-2 focus:ring-blue-100 outline-none px-3 py-2 rounded-sm text-sm ${mono ? "font-mono" : ""}`}
    />
  </label>
);

export default function Settings() {
  const { t } = useI18n();
  const [s, setS] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setS({ ...EMPTY, ...r.data }));
  }, []);

  const set = (k) => (e) => setS((prev) => ({ ...prev, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", s);
      toast.success("Ayarlar kaydedildi");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const onLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) {
      toast.error("Logo 500KB'dan büyük olamaz");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setS((prev) => ({ ...prev, company_logo_url: String(reader.result) }));
    reader.readAsDataURL(file);
  };

  return (
    <div>
      <PageHeader title={t("settings.title")} subtitle={t("settings.subtitle")}>
        <button onClick={save} disabled={saving} data-testid="save-settings"
          className="inline-flex items-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-60 text-white px-4 py-2 text-sm font-semibold rounded-sm">
          <FloppyDisk size={16} weight="bold" /> {saving ? "Kaydediliyor…" : t("settings.save")}
        </button>
      </PageHeader>

      <div className="p-4 sm:p-8 grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Company */}
        <div className="bg-white border border-slate-200">
          <div className="flex items-center gap-2 border-b border-slate-200 px-5 py-3">
            <Buildings size={18} weight="duotone" className="text-[#2563EB]" />
            <h3 className="font-display font-bold text-sm uppercase tracking-wider text-slate-700">{t("settings.company")}</h3>
          </div>
          <div className="p-5 grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Field label={t("settings.company_name")} value={s.company_name}
                onChange={set("company_name")}
                placeholder="Global Screen Exports A.Ş." testid="in-company-name" />
            </div>
            <div className="col-span-2">
              <label className="block">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{t("settings.address")}</div>
                <textarea rows={3} value={s.company_address || ""}
                  onChange={set("company_address")}
                  placeholder="Sokak, Bina, Şehir, Posta Kodu, Ülke"
                  data-testid="in-company-address"
                  className="mt-1 w-full border border-slate-300 focus:border-[#2563EB] focus:ring-2 focus:ring-blue-100 outline-none px-3 py-2 rounded-sm text-sm" />
              </label>
            </div>
            <Field label="Telefon" value={s.company_phone}
              onChange={set("company_phone")}
              placeholder="+90 212 555 0100" mono testid="in-company-phone" />
            <Field label="Email" value={s.company_email}
              onChange={set("company_email")}
              placeholder="export@..." testid="in-company-email" />
            <Field label="Website" value={s.company_website}
              onChange={set("company_website")}
              placeholder="https://www..." testid="in-company-website" />
            <Field label="Vergi No / Tax ID" value={s.company_tax_id}
              onChange={set("company_tax_id")}
              placeholder="1234567890" mono testid="in-company-tax" />
          </div>
        </div>

        {/* Logo */}
        <div className="bg-white border border-slate-200 h-fit">
          <div className="flex items-center gap-2 border-b border-slate-200 px-5 py-3">
            <ImageIcon size={18} weight="duotone" className="text-[#0F172A]" />
            <h3 className="font-display font-bold text-sm uppercase tracking-wider text-slate-700">{t("settings.logo")}</h3>
          </div>
          <div className="p-5 grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">
                Logo (opsiyonel, max 500KB PNG/JPG)
              </div>
              <div className="flex items-center gap-4 flex-wrap">
                <input type="file" accept="image/*" onChange={onLogoUpload}
                  data-testid="in-company-logo"
                  className="text-xs text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:border-0 file:bg-slate-100 file:text-slate-700 file:cursor-pointer" />
                {s.company_logo_url && (
                  <img src={s.company_logo_url} alt="Logo"
                    className="h-14 border border-slate-200 bg-white object-contain px-2" />
                )}
                {s.company_logo_url && (
                  <button onClick={() => setS((prev) => ({ ...prev, company_logo_url: "" }))}
                    className="text-xs text-red-600 hover:underline" data-testid="remove-logo">{t("settings.remove")}</button>
                )}
              </div>
            </div>
            <label className="block">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{t("settings.logo_size")}</div>
              <select value={s.logo_size || "medium"} onChange={set("logo_size")}
                data-testid="in-logo-size"
                className="mt-1 w-full border border-slate-300 focus:border-[#2563EB] focus:ring-2 focus:ring-blue-100 outline-none px-3 py-2 rounded-sm text-sm bg-white">
                <option value="small">{t("settings.small")}</option>
                <option value="medium">{t("settings.medium")}</option>
                <option value="large">{t("settings.large")}</option>
              </select>
            </label>
            <label className="block">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{t("settings.logo_position")}</div>
              <select value={s.logo_position || "left"} onChange={set("logo_position")}
                data-testid="in-logo-position"
                className="mt-1 w-full border border-slate-300 focus:border-[#2563EB] focus:ring-2 focus:ring-blue-100 outline-none px-3 py-2 rounded-sm text-sm bg-white">
                <option value="left">{t("settings.left")}</option>
                <option value="center">{t("settings.center")}</option>
                <option value="right">{t("settings.right")}</option>
              </select>
            </label>
          </div>
          <div className="border-t border-slate-200 px-5 py-3 bg-slate-50 text-xs text-slate-500">
            {t("settings.logo_help")}
          </div>
        </div>

        {/* Bank */}
        <div className="bg-white border border-slate-200 h-fit xl:col-span-2">
          <div className="flex items-center gap-2 border-b border-slate-200 px-5 py-3">
            <Bank size={18} weight="duotone" className="text-emerald-600" />
            <h3 className="font-display font-bold text-sm uppercase tracking-wider text-slate-700">{t("settings.bank_info")}</h3>
          </div>
          <div className="p-5 grid grid-cols-2 gap-4">
            <Field label={t("settings.bank_name")} value={s.bank_name}
              onChange={set("bank_name")}
              placeholder="Ziraat Bankası" testid="in-bank-name" />
            <Field label={t("settings.bank_branch")} value={s.bank_branch}
              onChange={set("bank_branch")}
              placeholder="İstanbul Merkez Şubesi" testid="in-bank-branch" />
            <div className="col-span-2">
              <Field label={t("settings.account_holder")} value={s.bank_account_holder}
                onChange={set("bank_account_holder")}
                placeholder="Şirket Adı A.Ş." testid="in-bank-holder" />
            </div>
            <div className="col-span-2">
              <Field label="IBAN" value={s.bank_iban}
                onChange={set("bank_iban")}
                placeholder="TR58 0001 0000 1234 5678 9012 34" mono testid="in-bank-iban" />
            </div>
            <Field label="SWIFT / BIC" value={s.bank_swift}
              onChange={set("bank_swift")}
              placeholder="TCZBTR2A" mono testid="in-bank-swift" />
          </div>
          <div className="border-t border-slate-200 px-5 py-3 bg-slate-50 text-xs text-slate-500">
            {t("settings.bank_help")}
          </div>
        </div>
      </div>
    </div>
  );
}
